import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../ConfirmDialog';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ClubDetailPage = ({ clubId, onBack, onLeave }) => {
  const { authFetch, user } = useAuth();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [groupSection, setGroupSection] = useState('members'); // For group toggle: 'members' or 'challenges'
  const [groupSearchQuery, setGroupSearchQuery] = useState(''); // Search within group members/challenges
  // Modal states
  const [showEventModal, setShowEventModal] = useState(false);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  // Add member search state
  const [memberSearch, setMemberSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingMember, setAddingMember] = useState(null);

  // Form states for modals
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'training',
    location: '',
    start_datetime: '',
    end_datetime: '',
    max_participants: ''
  });
  const [newsForm, setNewsForm] = useState({
    title: '',
    content: '',
    is_pinned: false
  });
  const [coachForm, setCoachForm] = useState({
    name: '',
    title: '',
    bio: '',
    specialization: '',
    email: '',
    phone: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: null, // 'leave', 'delete', 'promote', 'demote', 'remove', 'transfer'
    title: '',
    message: '',
    memberId: null,
    memberName: null
  });

  const isClub = club?.club_type === 'club';
  const isSuperAdmin = club?.my_role === 'super_admin';
  const isAdmin = club?.my_role === 'admin' || isSuperAdmin;

  const loadClubData = useCallback(async () => {
    setLoading(true);
    try {
      const [clubRes, membersRes, challengesRes] = await Promise.all([
        authFetch(`/api/clubs/${clubId}`),
        authFetch(`/api/clubs/${clubId}/members`),
        authFetch(`/api/clubs/${clubId}/challenges`)
      ]);

      if (clubRes.ok) {
        const data = await clubRes.json();
        setClub(data.club);

        // Load club-specific data if it's a formal club
        if (data.club.club_type === 'club') {
          const [eventsRes, newsRes, coachesRes] = await Promise.all([
            authFetch(`/api/clubs/${clubId}/events`),
            authFetch(`/api/clubs/${clubId}/news`),
            authFetch(`/api/clubs/${clubId}/coaches`)
          ]);

          if (eventsRes.ok) {
            const eventsData = await eventsRes.json();
            setEvents(eventsData.events || []);
          }
          if (newsRes.ok) {
            const newsData = await newsRes.json();
            setNews(newsData.news || []);
          }
          if (coachesRes.ok) {
            const coachesData = await coachesRes.json();
            setCoaches(coachesData.coaches || []);
          }
        }
      }

      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || []);
      }

      if (challengesRes.ok) {
        const data = await challengesRes.json();
        setChallenges(data.challenges || []);
      }
    } catch (err) {
      console.error('Failed to load club data:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, clubId]);

  useEffect(() => {
    loadClubData();
  }, [loadClubData]);

  // Set initial tab based on club type
  useEffect(() => {
    if (club) {
      setActiveTab(club.club_type === 'club' ? 'about' : 'members');
    }
  }, [club]);

  const showLeaveConfirm = () => {
    const typeLabel = isClub ? 'club' : 'group';
    setConfirmDialog({
      isOpen: true,
      type: 'leave',
      title: `Leave ${typeLabel}`,
      message: `Are you sure you want to leave this ${typeLabel}?`,
      memberId: null,
      memberName: null
    });
  };

  const handleLeave = async () => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/leave`, {
        method: 'POST'
      });

      if (res.ok) {
        onLeave();
      }
    } catch (err) {
      console.error('Failed to leave:', err);
    }
  };

  const handleEdit = () => {
    setEditForm({ name: club.name, description: club.description || '' });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const res = await authFetch(`/api/clubs/${clubId}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        setShowEditModal(false);
        loadClubData();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to update');
      }
    } catch (err) {
      setFormError('Failed to update');
    } finally {
      setFormLoading(false);
    }
  };

  const showDeleteConfirm = () => {
    const typeLabel = isClub ? 'club' : 'group';
    setConfirmDialog({
      isOpen: true,
      type: 'delete',
      title: `Delete ${typeLabel}`,
      message: `Are you sure you want to delete this ${typeLabel}? This action cannot be undone.`,
      memberId: null,
      memberName: null
    });
  };

  const handleDelete = async () => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        onBack();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to delete');
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Member management functions
  const showPromoteConfirm = (memberId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'promote',
      title: 'Promote Member',
      message: 'Promote this member to Admin?',
      memberId,
      memberName: null
    });
  };

  const handlePromoteMember = async (memberId) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/members/${memberId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: 'admin' })
      });
      if (res.ok) {
        loadClubData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to promote member');
      }
    } catch (err) {
      console.error('Failed to promote member:', err);
    }
  };

  const showDemoteConfirm = (memberId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'demote',
      title: 'Remove Admin Rights',
      message: 'Remove admin rights from this member?',
      memberId,
      memberName: null
    });
  };

  const handleDemoteMember = async (memberId) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/members/${memberId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: 'member' })
      });
      if (res.ok) {
        loadClubData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to demote member');
      }
    } catch (err) {
      console.error('Failed to demote member:', err);
    }
  };

  const showRemoveConfirm = (memberId) => {
    setConfirmDialog({
      isOpen: true,
      type: 'remove',
      title: 'Remove Member',
      message: 'Remove this member from the club?',
      memberId,
      memberName: null
    });
  };

  const handleRemoveMember = async (memberId) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/members/${memberId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        loadClubData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to remove member');
      }
    } catch (err) {
      console.error('Failed to remove member:', err);
    }
  };

  const showTransferConfirm = (memberId, memberName) => {
    setConfirmDialog({
      isOpen: true,
      type: 'transfer',
      title: 'Transfer Ownership',
      message: `Transfer ownership to ${memberName}? This cannot be undone.`,
      memberId,
      memberName
    });
  };

  const handleConfirmDialogAction = async () => {
    const { type, memberId } = confirmDialog;
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    switch (type) {
      case 'leave':
        await handleLeave();
        break;
      case 'delete':
        await handleDelete();
        break;
      case 'promote':
        await handlePromoteMember(memberId);
        break;
      case 'demote':
        await handleDemoteMember(memberId);
        break;
      case 'remove':
        await handleRemoveMember(memberId);
        break;
      case 'transfer':
        await handleTransferOwnership(memberId);
        break;
      default:
        break;
    }
  };

  const handleTransferOwnership = async (newOwnerId) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/transfer-ownership`, {
        method: 'POST',
        body: JSON.stringify({ new_super_admin_id: newOwnerId })
      });
      if (res.ok) {
        setShowTransferModal(false);
        loadClubData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to transfer ownership');
      }
    } catch (err) {
      console.error('Failed to transfer ownership:', err);
    }
  };

  const handleRSVP = async (eventId, status) => {
    try {
      const res = await authFetch(`/api/clubs/${clubId}/events/${eventId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        loadClubData();
      }
    } catch (err) {
      console.error('Failed to RSVP:', err);
    }
  };

  const handleSearchUsers = async (query) => {
    setMemberSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const res = await authFetch(`/api/clubs/${clubId}/search-users?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddMember = async (userId) => {
    setAddingMember(userId);
    try {
      const res = await authFetch(`/api/clubs/${clubId}/members/add`, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId })
      });
      if (res.ok) {
        // Remove from search results
        setSearchResults(prev => prev.filter(u => u.id !== userId));
        // Reload members
        loadClubData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to add member');
      }
    } catch (err) {
      console.error('Failed to add member:', err);
    } finally {
      setAddingMember(null);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        title: eventForm.title,
        description: eventForm.description || null,
        event_type: eventForm.event_type,
        location: eventForm.location || null,
        start_datetime: eventForm.start_datetime,
        end_datetime: eventForm.end_datetime || null,
        max_participants: eventForm.max_participants ? parseInt(eventForm.max_participants) : null
      };
      const res = await authFetch(`/api/clubs/${clubId}/events`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowEventModal(false);
        setEventForm({
          title: '', description: '', event_type: 'training',
          location: '', start_datetime: '', end_datetime: '', max_participants: ''
        });
        loadClubData();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to create event');
      }
    } catch (err) {
      setFormError('Failed to create event');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateNews = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const res = await authFetch(`/api/clubs/${clubId}/news`, {
        method: 'POST',
        body: JSON.stringify(newsForm)
      });
      if (res.ok) {
        setShowNewsModal(false);
        setNewsForm({ title: '', content: '', is_pinned: false });
        loadClubData();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to post news');
      }
    } catch (err) {
      setFormError('Failed to post news');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        name: coachForm.name,
        title: coachForm.title || null,
        bio: coachForm.bio || null,
        specialization: coachForm.specialization || null,
        email: coachForm.email || null,
        phone: coachForm.phone || null
      };
      const res = await authFetch(`/api/clubs/${clubId}/coaches`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setShowCoachModal(false);
        setCoachForm({ name: '', title: '', bio: '', specialization: '', email: '', phone: '' });
        loadClubData();
      } else {
        const data = await res.json();
        setFormError(data.detail || 'Failed to add coach');
      }
    } catch (err) {
      setFormError('Failed to add coach');
    } finally {
      setFormLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'super_admin') return 'Owner';
    if (role === 'admin') return 'Admin';
    return 'Member';
  };

  const getTabs = () => {
    if (isClub) {
      return [
        { id: 'about', label: 'About' },
        { id: 'events', label: 'Events' },
        { id: 'news', label: 'News' },
        { id: 'coaches', label: 'Coaches' },
        { id: 'members', label: 'Members' },
        { id: 'challenges', label: 'Challenges' }
      ];
    }
    return [
      { id: 'members', label: 'Members' },
      { id: 'challenges', label: 'Challenges' }
    ];
  };

  if (loading) {
    return (
      <div className="clubs-page">
        <div className="clubs-loading">Loading...</div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="clubs-page">
        <div className="clubs-empty">Not found</div>
      </div>
    );
  }

  return (
    <div className="clubs-page">
      <div className="clubs-container">
        <button className="back-button" onClick={onBack}>
          &larr; Back to Clubs/Groups
        </button>

        <div className="club-detail">
          <div className="club-detail__header">
            <div className="club-detail__info">
              <div className="club-detail__title-row">
                <h1>{club.name}</h1>
                {!isClub && (
                  <div className="club-detail__inline-stats">
                    <span className="inline-stat">{club.member_count || members.length} Members</span>
                    <span className="inline-stat">{challenges.length} Challenges</span>
                  </div>
                )}
              </div>
              {club.description && <p>{club.description}</p>}
            </div>
            <div className="club-detail__actions">
              {isSuperAdmin && (
                <>
                  <button className="btn-edit" onClick={handleEdit}>
                    Edit
                  </button>
                  <button className="btn-danger" onClick={showDeleteConfirm}>
                    Delete
                  </button>
                </>
              )}
              {!isSuperAdmin && (
                <button className="btn-danger" onClick={showLeaveConfirm}>
                  Leave
                </button>
              )}
            </div>
          </div>


          {isClub && (
            <div className="club-detail__stats">
              <div className="stat-item">
                <span className="stat-value">{club.member_count || members.length}</span>
                <span className="stat-label">Members</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{events.length}</span>
                <span className="stat-label">Events</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{coaches.length}</span>
                <span className="stat-label">Coaches</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{challenges.length}</span>
                <span className="stat-label">Challenges</span>
              </div>
            </div>
          )}

          {/* Tabs only for clubs */}
          {isClub && (
            <div className="club-tabs">
              {getTabs().map(tab => (
                <button
                  key={tab.id}
                  className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* About Tab (Club only) */}
          {activeTab === 'about' && isClub && (
            <div className="tab-content">
              {club.about && (
                <div className="about-section">
                  <h3>About</h3>
                  <p>{club.about}</p>
                </div>
              )}
              {club.location_address && (
                <div className="about-section">
                  <h3>Location</h3>
                  <p>{club.location_address}</p>
                </div>
              )}
              {(club.contact_email || club.contact_phone || club.website) && (
                <div className="about-section">
                  <h3>Contact</h3>
                  {club.contact_email && <p>Email: {club.contact_email}</p>}
                  {club.contact_phone && <p>Phone: {club.contact_phone}</p>}
                  {club.website && (
                    <p>Website: <a href={club.website} target="_blank" rel="noopener noreferrer">{club.website}</a></p>
                  )}
                </div>
              )}
              {!club.about && !club.location_address && !club.contact_email && (
                <div className="empty-state">No about information added yet</div>
              )}
            </div>
          )}

          {/* Events Tab (Club only) */}
          {activeTab === 'events' && isClub && (
            <div className="tab-content">
              {isAdmin && (
                <button className="btn-add" onClick={() => setShowEventModal(true)}>
                  + Add Event
                </button>
              )}
              {events.length === 0 ? (
                <div className="empty-state">No upcoming events</div>
              ) : (
                <div className="events-list">
                  {events.map(event => (
                    <div key={event.id} className="event-card">
                      <div className="event-card__header">
                        <span className="event-type">{event.event_type}</span>
                        <h4>{event.title}</h4>
                      </div>
                      <div className="event-card__details">
                        <p className="event-datetime">
                          {new Date(event.start_datetime).toLocaleDateString('en-GB', {
                            weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                        {event.location && <p className="event-location">{event.location}</p>}
                        {event.description && <p className="event-desc">{event.description}</p>}
                      </div>
                      <div className="event-card__footer">
                        <span className="event-participants">{event.participant_count || 0} attending</span>
                        <div className="rsvp-buttons">
                          <button
                            className={`rsvp-btn ${event.my_rsvp === 'attending' ? 'active' : ''}`}
                            onClick={() => handleRSVP(event.id, 'attending')}
                          >
                            Going
                          </button>
                          <button
                            className={`rsvp-btn ${event.my_rsvp === 'maybe' ? 'active' : ''}`}
                            onClick={() => handleRSVP(event.id, 'maybe')}
                          >
                            Maybe
                          </button>
                          <button
                            className={`rsvp-btn ${event.my_rsvp === 'declined' ? 'active' : ''}`}
                            onClick={() => handleRSVP(event.id, 'declined')}
                          >
                            Can't go
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* News Tab (Club only) */}
          {activeTab === 'news' && isClub && (
            <div className="tab-content">
              {isAdmin && (
                <button className="btn-add" onClick={() => setShowNewsModal(true)}>
                  + Post News
                </button>
              )}
              {news.length === 0 ? (
                <div className="empty-state">No news posts yet</div>
              ) : (
                <div className="news-list">
                  {news.map(item => (
                    <div key={item.id} className={`news-card ${item.is_pinned ? 'pinned' : ''}`}>
                      {item.is_pinned && <span className="pinned-badge">Pinned</span>}
                      <h4>{item.title}</h4>
                      <p className="news-content">{item.content}</p>
                      <div className="news-meta">
                        <span>By {item.author_name || 'Admin'}</span>
                        <span>{new Date(item.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Coaches Tab (Club only) */}
          {activeTab === 'coaches' && isClub && (
            <div className="tab-content">
              {isAdmin && (
                <button className="btn-add" onClick={() => setShowCoachModal(true)}>
                  + Add Coach
                </button>
              )}
              {coaches.length === 0 ? (
                <div className="empty-state">No coaches added yet</div>
              ) : (
                <div className="coaches-grid">
                  {coaches.map(coach => (
                    <div key={coach.id} className="coach-card">
                      <div className="coach-avatar">
                        {coach.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="coach-info">
                        <h4>{coach.name}</h4>
                        {coach.title && <span className="coach-title">{coach.title}</span>}
                        {coach.specialization && <span className="coach-spec">{coach.specialization}</span>}
                        {coach.bio && <p className="coach-bio">{coach.bio}</p>}
                        <div className="coach-contact">
                          {coach.email && <span>{coach.email}</span>}
                          {coach.phone && <span>{coach.phone}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Group Toggle Row */}
          {!isClub && (
            <div className="group-toggle-container">
              <div className="group-toggle-row">
                <div className="group-toggle">
                  <button
                    className={`group-toggle__btn ${groupSection === 'members' ? 'active' : ''}`}
                    onClick={() => { setGroupSection('members'); setGroupSearchQuery(''); }}
                  >
                    Members
                  </button>
                  <button
                    className={`group-toggle__btn group-toggle__btn--challenges ${groupSection === 'challenges' ? 'active' : ''}`}
                    onClick={() => { setGroupSection('challenges'); setGroupSearchQuery(''); }}
                  >
                    Challenges/Goals
                  </button>
                </div>
                {isAdmin && groupSection === 'members' && (
                  <button className="btn-add-right" onClick={() => setShowAddMemberModal(true)}>
                    Add Members
                  </button>
                )}
              </div>
              <div className="group-search">
                <svg className="group-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  className="group-search__input"
                  placeholder={groupSection === 'members' ? 'Search members...' : 'Search challenges...'}
                  value={groupSearchQuery}
                  onChange={(e) => setGroupSearchQuery(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Members Tab/Section */}
          {(activeTab === 'members' || (!isClub && groupSection === 'members')) && (isClub || groupSection === 'members') && (
            <div className="tab-content">
              {isClub && isAdmin && (
                <button className="btn-add" onClick={() => setShowAddMemberModal(true)}>
                  Add Members
                </button>
              )}
              {(() => {
                const filteredMembers = members.filter(member => {
                  if (!groupSearchQuery || isClub) return true;
                  const searchLower = groupSearchQuery.toLowerCase();
                  const name = (member.name || '').toLowerCase();
                  const email = (member.email || '').toLowerCase();
                  return name.includes(searchLower) || email.includes(searchLower);
                });
                return filteredMembers.length === 0 && groupSearchQuery ? (
                  <div className="empty-state">No members match your search</div>
                ) : (
                  <div className="members-list">
                    {filteredMembers.map(member => {
                      let profileImageUrl = member.profile_image;
                      if (profileImageUrl && !profileImageUrl.startsWith('http')) {
                        profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
                      }
                      return (
                        <div key={member.user_id} className="member-item">
                          <div className={`member-avatar ${profileImageUrl ? 'has-image' : ''}`}>
                            {profileImageUrl ? (
                              <img src={profileImageUrl} alt={member.name || 'Member'} />
                            ) : (
                              (member.name || member.email || '?').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="member-info">
                            <span className="member-name">{member.name || member.email}</span>
                            <span className={`member-role member-role--${member.role}`}>
                              {getRoleLabel(member.role)}
                            </span>
                          </div>
                          {isAdmin && member.user_id !== user?.id && (
                            <div className="member-actions">
                              {isSuperAdmin && member.role === 'member' && (
                                <button
                                  className="btn-small"
                                  onClick={() => showPromoteConfirm(member.user_id)}
                                >
                                  Make Admin
                                </button>
                              )}
                              {isSuperAdmin && member.role === 'admin' && (
                                <button
                                  className="btn-small"
                                  onClick={() => showDemoteConfirm(member.user_id)}
                                >
                                  Remove Admin
                                </button>
                              )}
                              {(isSuperAdmin || (isAdmin && member.role === 'member')) && (
                                <button
                                  className="btn-small btn-danger-small"
                                  onClick={() => showRemoveConfirm(member.user_id)}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Challenges Tab/Section */}
          {(activeTab === 'challenges' || (!isClub && groupSection === 'challenges')) && (isClub || groupSection === 'challenges') && (
            <div className="tab-content">
              {(() => {
                const filteredChallenges = challenges.filter(challenge => {
                  if (!groupSearchQuery || isClub) return true;
                  const searchLower = groupSearchQuery.toLowerCase();
                  const title = (challenge.title || '').toLowerCase();
                  return title.includes(searchLower);
                });
                return filteredChallenges.length === 0 ? (
                  <div className="empty-state">
                    {groupSearchQuery ? 'No challenges match your search' : 'No challenges yet'}
                  </div>
                ) : (
                  <div className="challenges-list">
                    {filteredChallenges.map(challenge => (
                      <div key={challenge.id} className="challenge-item">
                        <h4>{challenge.title}</h4>
                        <p>{challenge.target_value} {challenge.target_unit}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Transfer Ownership Modal */}
        {showTransferModal && (
          <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
            <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Transfer Ownership</h2>
                <button className="modal-close" onClick={() => setShowTransferModal(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p>Select a member to transfer ownership to:</p>
                <div className="transfer-members-list">
                  {members.filter(m => m.user_id !== user?.id).map(member => (
                    <button
                      key={member.user_id}
                      className="transfer-member-btn"
                      onClick={() => {
                        setShowTransferModal(false);
                        showTransferConfirm(member.user_id, member.name || member.email);
                      }}
                    >
                      <span>{member.name || member.email}</span>
                      <span className="member-role-small">{getRoleLabel(member.role)}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Event Modal */}
        {showEventModal && (
          <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Create Event</h2>
                <button className="modal-close" onClick={() => setShowEventModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleCreateEvent}>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="form-group">
                  <label>Event Title *</label>
                  <input
                    type="text"
                    value={eventForm.title}
                    onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="e.g., Saturday Morning Run"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Event Type</label>
                  <select
                    value={eventForm.event_type}
                    onChange={e => setEventForm({ ...eventForm, event_type: e.target.value })}
                  >
                    <option value="training">Training Session</option>
                    <option value="race">Race</option>
                    <option value="social">Social Event</option>
                    <option value="meeting">Meeting</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={eventForm.description}
                    onChange={e => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Describe the event..."
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={eventForm.location}
                    onChange={e => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="e.g., Central Park Main Entrance"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date/Time *</label>
                    <input
                      type="datetime-local"
                      value={eventForm.start_datetime}
                      onChange={e => setEventForm({ ...eventForm, start_datetime: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date/Time</label>
                    <input
                      type="datetime-local"
                      value={eventForm.end_datetime}
                      onChange={e => setEventForm({ ...eventForm, end_datetime: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Max Participants (optional)</label>
                  <input
                    type="number"
                    value={eventForm.max_participants}
                    onChange={e => setEventForm({ ...eventForm, max_participants: e.target.value })}
                    placeholder="Leave empty for unlimited"
                    min="1"
                  />
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEventModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={formLoading}>
                    {formLoading ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create News Modal */}
        {showNewsModal && (
          <div className="modal-overlay" onClick={() => setShowNewsModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Post News</h2>
                <button className="modal-close" onClick={() => setShowNewsModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleCreateNews}>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={newsForm.title}
                    onChange={e => setNewsForm({ ...newsForm, title: e.target.value })}
                    placeholder="News headline..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Content *</label>
                  <textarea
                    value={newsForm.content}
                    onChange={e => setNewsForm({ ...newsForm, content: e.target.value })}
                    placeholder="Write your news post..."
                    rows={5}
                    required
                  />
                </div>

                <div className="form-group form-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={newsForm.is_pinned}
                      onChange={e => setNewsForm({ ...newsForm, is_pinned: e.target.checked })}
                    />
                    <span>Pin this post</span>
                  </label>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowNewsModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={formLoading}>
                    {formLoading ? 'Posting...' : 'Post News'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Coach Modal */}
        {showCoachModal && (
          <div className="modal-overlay" onClick={() => setShowCoachModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Coach</h2>
                <button className="modal-close" onClick={() => setShowCoachModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleCreateCoach}>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={coachForm.name}
                    onChange={e => setCoachForm({ ...coachForm, name: e.target.value })}
                    placeholder="Coach name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Title / Role</label>
                  <input
                    type="text"
                    value={coachForm.title}
                    onChange={e => setCoachForm({ ...coachForm, title: e.target.value })}
                    placeholder="e.g., Head Coach, Running Coach"
                  />
                </div>

                <div className="form-group">
                  <label>Specialization</label>
                  <input
                    type="text"
                    value={coachForm.specialization}
                    onChange={e => setCoachForm({ ...coachForm, specialization: e.target.value })}
                    placeholder="e.g., Marathon Training, Speed Work"
                  />
                </div>

                <div className="form-group">
                  <label>Bio</label>
                  <textarea
                    value={coachForm.bio}
                    onChange={e => setCoachForm({ ...coachForm, bio: e.target.value })}
                    placeholder="Brief biography..."
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={coachForm.email}
                      onChange={e => setCoachForm({ ...coachForm, email: e.target.value })}
                      placeholder="coach@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={coachForm.phone}
                      onChange={e => setCoachForm({ ...coachForm, phone: e.target.value })}
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowCoachModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={formLoading}>
                    {formLoading ? 'Adding...' : 'Add Coach'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Member Modal */}
        {showAddMemberModal && (
          <div className="modal-overlay" onClick={() => { setShowAddMemberModal(false); setMemberSearch(''); setSearchResults([]); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Members</h2>
                <button className="modal-close" onClick={() => { setShowAddMemberModal(false); setMemberSearch(''); setSearchResults([]); }}>&times;</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Search by email or name</label>
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={e => handleSearchUsers(e.target.value)}
                    placeholder="Enter email or name..."
                    autoFocus
                  />
                </div>

                {searchLoading && (
                  <div className="search-loading">Searching...</div>
                )}

                {!searchLoading && memberSearch.length >= 2 && searchResults.length === 0 && (
                  <div className="search-empty">No users found</div>
                )}

                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(searchUser => (
                      <div key={searchUser.id} className="search-result-item">
                        <div className="search-result-avatar">
                          {(searchUser.name || searchUser.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="search-result-info">
                          <span className="search-result-name">{searchUser.name || 'No name'}</span>
                          <span className="search-result-email">{searchUser.email}</span>
                        </div>
                        <button
                          className="btn-add-small"
                          onClick={() => handleAddMember(searchUser.id)}
                          disabled={addingMember === searchUser.id}
                        >
                          {addingMember === searchUser.id ? 'Adding...' : 'Add'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Group/Club Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content modal-small" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit {isClub ? 'Club' : 'Group'}</h2>
                <button className="modal-close" onClick={() => setShowEditModal(false)}>&times;</button>
              </div>
              <form onSubmit={handleSaveEdit}>
                {formError && <div className="modal-error">{formError}</div>}

                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="transfer-ownership-section">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => {
                      setShowEditModal(false);
                      setShowTransferModal(true);
                    }}
                  >
                    Transfer Ownership
                  </button>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={formLoading}>
                    {formLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.type === 'leave' ? 'Leave' : confirmDialog.type === 'transfer' ? 'Transfer' : 'Confirm'}
          cancelText="Cancel"
          variant={confirmDialog.type === 'delete' || confirmDialog.type === 'remove' ? 'danger' : 'warning'}
          onConfirm={handleConfirmDialogAction}
          onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        />
      </div>

      <style>{`
        .back-button {
          background: none;
          border: none;
          color: var(--primary);
          font-size: 1rem;
          cursor: pointer;
          margin-bottom: 1.5rem;
          padding: 0;
        }

        .back-button:hover {
          opacity: 0.8;
        }

        .club-detail {
          background: var(--bg-card);
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          padding: 2rem;
        }

        .club-detail__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .club-detail__info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .club-detail__info h1 {
          font-family: 'Archivo Black', sans-serif;
          font-size: 1.75rem;
          color: var(--primary);
          margin: 0;
        }

        .club-detail__title-row {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .club-detail__inline-stats {
          display: flex;
          gap: 0.75rem;
        }

        .inline-stat {
          font-size: 0.875rem;
          color: #0D0D0D;
          background: var(--primary);
          padding: 0.375rem 1rem;
          border-radius: 12px;
          font-weight: 500;
        }

        .club-detail__info p {
          color: var(--text-light);
          margin: 0;
        }

        .club-detail__actions {
          display: flex;
          gap: 0.75rem;
        }

        .club-detail__actions button {
          padding: 0.625rem 1.25rem;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
        }

        .btn-secondary {
          background: var(--bg-surface);
          color: var(--text);
          border: 1px solid var(--border);
        }

        .btn-secondary:hover {
          background: var(--bg-card-hover);
        }

        .btn-edit {
          background: var(--bg-surface);
          color: var(--primary);
          border: 1px solid var(--border);
        }

        .btn-edit:hover {
          background: var(--bg-card-hover);
          border-color: var(--primary);
        }

        .btn-link {
          background: none;
          border: none;
          color: var(--text-light);
          font-size: 0.875rem;
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
        }

        .btn-link:hover {
          color: var(--primary);
        }

        .transfer-ownership-section {
          padding: 1rem 0;
          border-top: 1px solid var(--border);
          margin-top: 0.5rem;
        }

        .btn-danger {
          background: #dc2626;
          color: white;
        }

        .btn-danger:hover {
          background: #b91c1c;
        }

        .invite-code-box {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-surface);
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .invite-code {
          font-family: monospace;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--primary);
        }

        .btn-copy {
          padding: 0.5rem 1rem;
          background: var(--primary);
          color: #0D0D0D;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
        }

        .club-detail__stats {
          display: flex;
          gap: 2rem;
          padding: 1.5rem 0;
          border-bottom: 1px solid var(--border);
          margin-bottom: 1.5rem;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
        }

        .stat-value {
          font-family: 'Archivo Black', sans-serif;
          font-size: 2rem;
          color: var(--primary);
        }

        .stat-label {
          font-size: 0.875rem;
          color: var(--text-light);
        }

        .club-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .club-tabs .tab {
          padding: 0.75rem 1.5rem;
          background: var(--bg-surface);
          border: none;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          color: var(--text-light);
          cursor: pointer;
          transition: all 0.2s;
        }

        .club-tabs .tab:hover {
          color: var(--text);
        }

        .club-tabs .tab.active {
          background: var(--primary);
          color: #0D0D0D;
        }

        .tab-content {
          min-height: 200px;
        }

        .btn-add {
          display: inline-block;
          padding: 0.75rem 1.5rem;
          background: var(--primary);
          color: #0D0D0D;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          margin-bottom: 1.5rem;
        }

        .group-toggle-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .group-toggle-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .group-toggle {
          display: flex;
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 0.25rem;
        }

        .group-search {
          position: relative;
          display: flex;
          align-items: center;
        }

        .group-search__icon {
          position: absolute;
          left: 0.75rem;
          width: 16px;
          height: 16px;
          color: var(--text-light);
          pointer-events: none;
        }

        .group-search__input {
          padding: 0.5rem 0.75rem 0.5rem 2.25rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--bg-surface);
          color: var(--text);
          font-size: 0.875rem;
          width: 180px;
          transition: border-color 0.2s ease;
        }

        .group-search__input:focus {
          outline: none;
          border-color: var(--primary);
        }

        .group-search__input::placeholder {
          color: var(--text-light);
        }

        .group-toggle__btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 10px;
          font-size: 0.9375rem;
          font-weight: 600;
          cursor: pointer;
          background: transparent;
          color: var(--text-light);
          transition: all 0.2s ease;
        }

        .group-toggle__btn.active {
          background: var(--primary);
          color: #0D0D0D;
        }

        .group-toggle__btn:not(.active):hover {
          color: var(--text);
        }

        .group-toggle__btn--challenges:not(.active) {
          color: var(--primary);
        }

        .section-action-row {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 1rem;
        }

        .section-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .section-title {
          font-family: 'Archivo Black', sans-serif;
          font-size: 1.125rem;
          color: var(--text);
          margin: 0;
        }

        .btn-add-right {
          padding: 0.625rem 1.25rem;
          background: var(--primary);
          color: #0D0D0D;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-add-right:hover {
          opacity: 0.9;
        }

        .empty-state {
          padding: 3rem;
          text-align: center;
          color: var(--text-light);
        }

        /* About Tab */
        .about-section {
          margin-bottom: 1.5rem;
          padding: 1rem;
          background: var(--bg-surface);
          border-radius: 8px;
        }

        .about-section h3 {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--primary);
          text-transform: uppercase;
          margin: 0 0 0.5rem 0;
        }

        .about-section p {
          color: var(--text);
          margin: 0.25rem 0;
        }

        .about-section a {
          color: var(--primary);
        }

        /* Events Tab */
        .events-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .event-card {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 1.25rem;
          border-left: 4px solid var(--primary);
        }

        .event-card__header {
          margin-bottom: 0.75rem;
        }

        .event-type {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          background: rgba(255, 215, 0, 0.15);
          color: var(--primary);
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          margin-bottom: 0.5rem;
        }

        .event-card h4 {
          margin: 0;
          color: var(--text);
          font-size: 1.125rem;
        }

        .event-datetime {
          color: var(--primary);
          font-weight: 600;
          margin: 0 0 0.25rem 0;
        }

        .event-location, .event-desc {
          color: var(--text-light);
          margin: 0.25rem 0;
          font-size: 0.9375rem;
        }

        .event-card__footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
        }

        .event-participants {
          color: var(--text-light);
          font-size: 0.875rem;
        }

        .rsvp-buttons {
          display: flex;
          gap: 0.5rem;
        }

        .rsvp-btn {
          padding: 0.375rem 0.75rem;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-light);
          cursor: pointer;
        }

        .rsvp-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .rsvp-btn.active {
          background: var(--primary);
          border-color: var(--primary);
          color: #0D0D0D;
        }

        /* News Tab */
        .news-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .news-card {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 1.25rem;
          position: relative;
        }

        .news-card.pinned {
          border: 1px solid var(--primary);
        }

        .pinned-badge {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          padding: 0.25rem 0.5rem;
          background: var(--primary);
          color: #0D0D0D;
          border-radius: 4px;
          font-size: 0.625rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .news-card h4 {
          margin: 0 0 0.5rem 0;
          color: var(--text);
        }

        .news-content {
          color: var(--text-light);
          margin: 0 0 1rem 0;
          line-height: 1.5;
        }

        .news-meta {
          display: flex;
          gap: 1rem;
          font-size: 0.75rem;
          color: var(--text-light);
        }

        /* Coaches Tab */
        .coaches-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
        }

        .coach-card {
          background: var(--bg-surface);
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          gap: 1rem;
        }

        .coach-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: var(--primary);
          color: #0D0D0D;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .coach-info {
          flex: 1;
        }

        .coach-info h4 {
          margin: 0;
          color: var(--text);
        }

        .coach-title, .coach-spec {
          display: block;
          font-size: 0.8125rem;
          color: var(--text-light);
        }

        .coach-title {
          color: var(--primary);
          font-weight: 600;
        }

        .coach-bio {
          margin: 0.5rem 0;
          font-size: 0.875rem;
          color: var(--text-light);
          line-height: 1.4;
        }

        .coach-contact {
          display: flex;
          flex-direction: column;
          font-size: 0.75rem;
          color: var(--text-light);
          margin-top: 0.5rem;
        }

        /* Members Tab */
        .members-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .member-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem;
          background: var(--bg-surface);
          border-radius: 8px;
        }

        .member-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--primary);
          color: #0D0D0D;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          flex-shrink: 0;
          overflow: hidden;
        }

        .member-avatar.has-image {
          background: var(--bg-surface);
        }

        .member-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .member-info {
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .member-name {
          font-weight: 600;
          color: var(--text);
        }

        .member-role {
          font-size: 0.75rem;
          color: var(--text-light);
        }

        .member-role--super_admin {
          color: var(--primary);
          font-weight: 600;
        }

        .member-role--admin {
          color: #818cf8;
        }

        .member-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-small {
          padding: 0.375rem 0.75rem;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--primary);
          cursor: pointer;
        }

        .btn-small:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .btn-danger-small {
          background: #dc2626;
          border-color: #dc2626;
          color: white;
        }

        .btn-danger-small:hover {
          background: #b91c1c;
          border-color: #b91c1c;
        }

        /* Search Members */
        .search-loading, .search-empty {
          padding: 1rem;
          text-align: center;
          color: var(--text-light);
        }

        .search-results {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 300px;
          overflow-y: auto;
        }

        .search-result-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: var(--bg-surface);
          border-radius: 8px;
        }

        .search-result-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--border);
          color: var(--text);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          flex-shrink: 0;
        }

        .search-result-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .search-result-name {
          font-weight: 600;
          color: var(--text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .search-result-email {
          font-size: 0.8125rem;
          color: var(--text-light);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .btn-add-small {
          padding: 0.375rem 0.75rem;
          background: var(--primary);
          border: none;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #0D0D0D;
          cursor: pointer;
          flex-shrink: 0;
        }

        .btn-add-small:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-add-small:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Challenges Tab */
        .challenges-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .challenge-item {
          padding: 1rem;
          background: var(--bg-surface);
          border-radius: 8px;
        }

        .challenge-item h4 {
          margin: 0 0 0.25rem 0;
          color: var(--text);
        }

        .challenge-item p {
          margin: 0;
          color: var(--text-light);
          font-size: 0.875rem;
        }

        /* Transfer Modal */
        .transfer-members-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .transfer-member-btn {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          cursor: pointer;
          color: var(--text);
        }

        .transfer-member-btn:hover {
          border-color: var(--primary);
        }

        .member-role-small {
          font-size: 0.75rem;
          color: var(--text-light);
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 1rem;
        }

        .modal-content {
          background: var(--bg-card);
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-small {
          max-width: 400px;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--border);
        }

        .modal-header h2 {
          font-family: 'Archivo Black', sans-serif;
          font-size: 1.25rem;
          color: var(--primary);
          margin: 0;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: var(--text-light);
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .modal-close:hover {
          color: var(--text);
        }

        .modal-body {
          padding: 1.5rem;
        }

        .modal-body p {
          color: var(--text-light);
          margin: 0;
        }

        .modal-error {
          background: rgba(220, 38, 38, 0.15);
          color: #f87171;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin: 1rem 1.5rem;
          font-size: 0.875rem;
        }

        .modal-content form {
          padding: 1.5rem;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-light);
          margin-bottom: 0.5rem;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 0.75rem 1rem;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 1rem;
          color: var(--text);
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group textarea:focus,
        .form-group select:focus {
          outline: none;
          border-color: var(--primary);
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-group select {
          cursor: pointer;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-checkbox {
          display: flex;
          align-items: center;
        }

        .form-checkbox label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          margin-bottom: 0;
        }

        .form-checkbox input[type="checkbox"] {
          width: auto;
          cursor: pointer;
        }

        .form-checkbox span {
          font-weight: 400;
          color: var(--text);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding-top: 1rem;
          border-top: 1px solid var(--border);
          margin-top: 1rem;
        }

        .btn-cancel {
          padding: 0.75rem 1.5rem;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--text);
          cursor: pointer;
        }

        .btn-cancel:hover {
          background: var(--bg-card-hover);
        }

        .btn-submit {
          padding: 0.75rem 1.5rem;
          background: var(--primary);
          border: none;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 600;
          color: #0D0D0D;
          cursor: pointer;
        }

        .btn-submit:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .club-detail {
            padding: 1.25rem;
          }

          .club-detail__header {
            flex-direction: column;
            gap: 1rem;
          }

          .club-detail__actions {
            width: 100%;
          }

          .club-detail__actions button {
            flex: 1;
          }

          .club-detail__stats {
            gap: 1rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .club-tabs {
            gap: 0.375rem;
          }

          .club-tabs .tab {
            padding: 0.625rem 1rem;
            font-size: 0.875rem;
          }

          .member-actions {
            flex-direction: column;
          }

          .event-card__footer {
            flex-direction: column;
            gap: 0.75rem;
            align-items: flex-start;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .modal-content {
            max-height: 85vh;
          }
        }
      `}</style>
    </div>
  );
};

export default ClubDetailPage;
